import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize, forkJoin, of, Subject, takeUntil } from 'rxjs';

import { environment } from '../../../../../../environments/environment';
import { AuditContextService } from '../../../../audit-logs/audit-context.service';
import { AuditFieldChange } from '../../../../audit-logs/audit-logs.interface';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { BehaviourListModel } from '../../../../../core/models/common.models';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { LabelKey } from '../../../../../core/models/report-labels.model';
import { ReportLabelPipe } from '../../../../../core/pipes/report-label.pipe';
import { AuthService } from '../../../../../core/services/auth.service';
import { CmsRoleAccessService } from '../../../../../core/services/cms-role-access.service';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../core/services/loader.service';
import { DrawerHeaderComponent } from '../../../../../components/drawer-header/drawer-header.component';
import { TruncatePipe } from '../../../../../shared/pipes/truncate.pipe';
import {
  BipTemplateGatingService,
  BipTileSection,
} from '../../../../../shared/services/bip-template-gating.service';
import { TemplateModel } from '../../../../bip-template/bip-template.interface';
import { CommonService } from '../../../../../shared/services/common.service';
import { CustomQuestionService } from '../../../../manage-settings/custom-question/custom-question.service';
import {
  behaviorGoals,
  QuestionItem,
} from '../../../../manage-settings/manage-settings.interface';
import { ManageSettingsService } from '../../../../manage-settings/manage-settings.service';
import { UserRole } from '../../../../activity-dashboard/activity-dashboard.enums';
import {
  BehaviorFrequency,
  DraftStepType,
  InsightMode,
} from '../../../insights.enums';
import {
  BehaviorDetailModel,
  BehaviorModel,
  BehaviorPendingItem,
  BehaviorValidationStatus,
  CaseBehaviour,
  CaseDetailModel,
  caseBehaviorTypeDescriptions,
} from '../../../insights.interface';
import { FbaAssessmentComponent } from '../assessment-fba/fba-assessment/fba-assessment.component';
import { ManageBehaviorStrategiesComponent } from '../manage-behavior-strategies/manage-behavior-strategies.component';
import { InsightsDataComponent } from '../../insights-data/insights-data.component';
import { BehaviorService } from '../define-behavior/define-behavior.service';
import behaviorGoalExamples from '../define-behavior/goalsExample.json';
import { APP_LOOKUP_IDS } from '../print-bip/behavior-section-and-lookup-mapping.constant';
import {
  DefineBehaviorWizardDrawerParams,
  WizardStep,
  WizardStepKind,
} from './define-behavior-wizard.interface';
import { DefineBehaviorWizardService } from './define-behavior-wizard.service';
import {
  DefineBehaviorWizardSummaryComponent,
  WizardSummaryContent,
  WizardSummarySection,
} from './summary/define-behavior-wizard-summary.component';
import { DefineBehaviorWizardAntecedentsStepComponent } from './steps/antecedents-step/define-behavior-wizard-antecedents-step.component';
import { DefineBehaviorWizardBehaviorTypesStepComponent } from './steps/behavior-types-step/define-behavior-wizard-behavior-types-step.component';
import { DefineBehaviorWizardConsequencesStepComponent } from './steps/consequences-step/define-behavior-wizard-consequences-step.component';
import { DefineBehaviorWizardEnvironmentsStepComponent } from './steps/environments-step/define-behavior-wizard-environments-step.component';
import { DefineBehaviorWizardFrequencyStepComponent } from './steps/frequency-step/define-behavior-wizard-frequency-step.component';
import { DefineBehaviorWizardGoalsStepComponent } from './steps/goals-step/define-behavior-wizard-goals-step.component';
import { DefineBehaviorWizardInfoModalsComponent } from './steps/info-modals/define-behavior-wizard-info-modals.component';
import { DefineBehaviorWizardIntensityStepComponent } from './steps/intensity-step/define-behavior-wizard-intensity-step.component';
import { DefineBehaviorWizardOperationalDefinitionStepComponent } from './steps/operational-definition-step/define-behavior-wizard-operational-definition-step.component';
import { DefineBehaviorWizardSettingEventsStepComponent } from './steps/setting-events-step/define-behavior-wizard-setting-events-step.component';

@Component({
  selector: 'app-define-behavior-wizard',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzButtonModule,
    NzRadioModule,
    NzInputModule,
    NzSpinModule,
    NzToolTipModule,
    SvgIconComponent,
    ReportLabelPipe,
    TruncatePipe,
    DrawerHeaderComponent,
    DefineBehaviorWizardSummaryComponent,
    DefineBehaviorWizardBehaviorTypesStepComponent,
    DefineBehaviorWizardOperationalDefinitionStepComponent,
    DefineBehaviorWizardFrequencyStepComponent,
    DefineBehaviorWizardIntensityStepComponent,
    DefineBehaviorWizardEnvironmentsStepComponent,
    DefineBehaviorWizardSettingEventsStepComponent,
    DefineBehaviorWizardAntecedentsStepComponent,
    DefineBehaviorWizardConsequencesStepComponent,
    DefineBehaviorWizardGoalsStepComponent,
    DefineBehaviorWizardInfoModalsComponent,
  ],
  templateUrl: './define-behavior-wizard.component.html',
  styleUrl: './define-behavior-wizard.component.scss',
})
export class DefineBehaviorWizardComponent implements OnInit, OnDestroy {
  @ViewChild('drawerFBATitleTpl') drawerFBATitleTpl!: TemplateRef<void>;
  @ViewChild('drawerTitleBehaviorStrategies')
  drawerTitleBehaviorStrategies!: TemplateRef<void>;
  @ViewChild('drawerTitleData') drawerTitleData!: TemplateRef<void>;

  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly drawerService = inject(DrawerService);
  private readonly loaderService = inject(LoaderService);
  private readonly commonService = inject(CommonService);
  private readonly manageSettingService = inject(ManageSettingsService);
  private readonly customQuestionService = inject(CustomQuestionService);
  private readonly bipTemplateGatingService = inject(BipTemplateGatingService);
  private readonly behaviorService = inject(BehaviorService);
  private readonly wizardService = inject(DefineBehaviorWizardService);
  private readonly authService = inject(AuthService);
  private readonly cmsRoleAccessService = inject(CmsRoleAccessService);
  private readonly auditContextService = inject(AuditContextService);

  private readonly destroy$ = new Subject<void>();

  readonly lookupIds = APP_LOOKUP_IDS;
  readonly WizardStepKind = WizardStepKind;
  readonly behaviorGoalExamples = behaviorGoalExamples;
  readonly roleEnum = UserRole;
  assessmentLabel: string = LabelKey.ASSESS_LABEL_OVERRIDE;
  tileSection = BipTileSection;
  bipTemplate: TemplateModel | null = null;

  private readonly baseValidators: Record<string, ValidatorFn[]> = {
    operationalDefinition: [Validators.maxLength(4000)],
    behaviourFrequencyCount: [
      Validators.pattern('^[0-9]+(?:\\.[0-9]+)?$'),
      Validators.maxLength(6),
    ],
    goals: [Validators.maxLength(4000)],
  };

  private readonly formControls = {
    caseId: [null],
    behaviorCategoryId: [null],
    caseBehaviorTypeIds: [[] as number[]],
    caseBehaviorTypeDescriptions: this.fb.array([]),
    operationalDefinition: ['', this.baseValidators['operationalDefinition']],
    behaviourFrequencyId: [null],
    behaviourFrequencyCount: [
      { value: null, disabled: true },
      this.baseValidators['behaviourFrequencyCount'],
    ],
    isDurationRequired: [{ value: null, disabled: true }],
    duration: [
      { value: null, disabled: true },
      [Validators.pattern('^[0-9]*\\.?[0-9]*$'), Validators.maxLength(6)],
    ],
    behaviourIntensityId: [null],
    caseBehaviorEnvironmentIds: [[] as number[]],
    caseBehaviorSettingEventIds: [[] as number[]],
    caseBehaviorAntecedentIds: [[] as number[]],
    caseBehaviorAntecedentDescription: this.fb.array([]),
    caseBehaviorConsequenceIds: [[] as number[]],
    caseBehaviorConsequenceDescription: this.fb.array([]),
    goals: ['', this.baseValidators['goals']],
    caseBehaviorCustomQuestionAnswer: this.fb.array([]),
    draft: [true],
    draftStep: [0 as number | null],
    draftStepType: [DraftStepType.ApplicationObject as number | null],
  };

  form: FormGroup = this.fb.group(this.formControls);

  caseParams: DefineBehaviorWizardDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.ADD_BEHAVIOR,
    behaviorId: undefined,
  };

  readonly loading$ = this.loaderService.loading$;

  steps: WizardStep[] = [];
  currentIndex = 0;
  isSaving = false;
  isContinueProcessing = false;
  allowManageBehavior = true;
  // True once the user has stepped back from Summary (via an edit pencil or
  // the Previous button) to touch up an earlier step. Drives Save & Next vs.
  // Save & Preview independently of isBehaviorDraft — otherwise, editing an
  // already-satisfied step on a behavior that still has some *other* pending
  // Required/Hard-Stop item forces the user through Save & Next on every
  // remaining step just to get back to Summary, instead of jumping straight
  // back the way they came.
  cameFromSummary = false;

  behaviorCategoryList: BehaviourListModel[] = [];
  behaviourSubCategoryList: BehaviourListModel[] = [];
  behaviourFrequencyList: BehaviourListModel[] = [];
  behaviourIntensityList: BehaviourListModel[] = [];
  behaviourEnvironmentList: BehaviourListModel[] = [];
  behaviourSettingEventList: BehaviourListModel[] = [];
  behaviourAntecedentList: BehaviourListModel[] = [];
  behaviorConsequenceList: BehaviourListModel[] = [];
  templateCustomQuestions: QuestionItem[] = [];
  behaviorGoal: behaviorGoals | null = null;
  behaviorDetail: BehaviorDetailModel | null = null;
  // Fetched when the wizard reaches Summary — the backend is the source of
  // truth for which Required/Hard-Stop sections are still outstanding (see
  // loadValidationStatus()); null until the API responds or if it errors.
  validationStatus: BehaviorValidationStatus | null = null;

  isFunctionalEquivalentModalVisible = false;
  isSettingEventModalVisible = false;
  isGoalExampleModalVisible = false;
  isPdfModalVisible = false;
  isIntensityWarningModalVisible = false;

  private readonly questionAnswerIndex = new Map<number, number>();
  private selectedCategoryId: number | null = null;
  private frequencyStepRequired = false;
  private auditBaselineBehaviorDetail: BehaviorDetailModel | null = null;
  private lastModifiedFields: AuditFieldChange[] = [];

  ngOnInit(): void {
    this.authService.userRoles$
      .pipe(takeUntil(this.destroy$))
      .subscribe(roles => {
        if (roles) {
          this.allowManageBehavior =
            this.cmsRoleAccessService.computeAllowFromCmsRole(roles, [
              this.roleEnum.CMSManager,
              this.roleEnum.CMSDataEntry,
              this.roleEnum.CMSBrowser,
            ]);
          this.cdr.markForCheck();
        }
      });

    this.wireFormReactions();

    this.loaderService.show();
    this.drawerService
      .getParams<DefineBehaviorWizardDrawerParams>(
        DRAWER_IDS.DEFINE_BEHAVIOR_V2
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.loadWizardData(params);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private wireFormReactions(): void {
    this.form.controls['behaviorCategoryId'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: number) => {
        if (this.selectedCategoryId === value) {
          return;
        }
        this.selectedCategoryId = value;
        this.behaviourSubCategoryList = [];
        this.form.controls['caseBehaviorTypeIds'].setValue([]);
        this.behaviorTypeDescriptions.clear();
        if (value) {
          this.loadBehaviourSubTypes(value);
        }
        this.cdr.markForCheck();
      });

    this.form.controls['behaviourFrequencyId'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: number) => {
        const countControl = this.form.controls['behaviourFrequencyCount'];
        const durationRequiredControl =
          this.form.controls['isDurationRequired'];
        if (value) {
          countControl.enable({ emitEvent: false });
          durationRequiredControl.enable({ emitEvent: false });
        } else {
          countControl.setValue(null);
          durationRequiredControl.setValue(null);
          countControl.disable({ emitEvent: false });
          durationRequiredControl.disable({ emitEvent: false });
        }
        this.updateFrequencyValidators();
      });

    this.form.controls['isDurationRequired'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        const durationControl = this.form.controls['duration'];
        if (value === true) {
          durationControl.enable({ emitEvent: false });
        } else {
          durationControl.setValue(null);
          durationControl.disable({ emitEvent: false });
        }
        this.updateDurationValidators();
      });
  }

  private loadWizardData(params: DefineBehaviorWizardDrawerParams): void {
    this.caseParams = params;
    // caseId belongs to the case, not the mode — must be set for edit/view
    // too, or it stays null from the form's initial default and the backend
    // rejects the save ("Error converting value {null} to type 'Int64'").
    this.form.get('caseId')?.setValue(params.case?.id);

    const organizationId = params.case?.student?.organizationId;
    const bipTemplateId = params.case?.bipTemplateId;

    forkJoin({
      lookups: this.commonService.getBehaviourLookups(),
      goals: this.manageSettingService.getbehaviorGoalsByOrgId(organizationId),
      template: bipTemplateId
        ? this.bipTemplateGatingService.getTemplate(bipTemplateId)
        : of(null),
      customQuestions: bipTemplateId
        ? this.customQuestionService.getQuestionsByTemplateId(bipTemplateId)
        : of({
            result: [] as QuestionItem[],
            message: null,
            statusCode: 200,
            errors: [],
          }),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ lookups, goals, template, customQuestions }) => {
        // Stored for the Summary step's Continue button — see nextContinueTile — which needs
        // Assessment/Strategy/Data inclusion. Steps themselves stay Challenging-Behavior-only.
        this.bipTemplate = template;
        if (lookups?.result) {
          this.behaviorCategoryList = lookups.result.behaviorCategory;
          this.behaviourFrequencyList = lookups.result.behaviourFrequency;
          this.behaviourIntensityList = lookups.result.behaviourIntensity;
          this.behaviourEnvironmentList = lookups.result.behaviourEnvironment;
          this.behaviourSettingEventList = lookups.result.behaviourSettingEvent;
          this.behaviourAntecedentList = lookups.result.behaviourAntecedent;
          this.behaviorConsequenceList = lookups.result.behaviorConsequence;
        }
        this.behaviorGoal = goals?.result ?? null;
        this.templateCustomQuestions = customQuestions?.result ?? [];

        this.steps = this.wizardService.buildSteps({
          template,
          templateCustomQuestions: this.templateCustomQuestions,
        });
        this.frequencyStepRequired = !!this.steps.find(
          step => step.refId === APP_LOOKUP_IDS.Frequency
        )?.isRequired;

        this.populateCustomQuestionAnswers();
        this.applyStepValidators();
        this.updateFrequencyValidators();

        if (this.isEditOrViewMode() && this.caseParams.behaviorId) {
          this.loadExistingBehavior(this.caseParams.behaviorId);
        } else {
          this.loaderService.hide();
          this.cdr.markForCheck();
        }
      });
  }

  private loadExistingBehavior(behaviorId: number): void {
    this.behaviorService
      .getBehaviorById(behaviorId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe(response => {
        if (response.result) {
          this.behaviorDetail = response.result;
          this.auditBaselineBehaviorDetail = structuredClone(response.result);
          this.patchExistingBehavior(response.result);
          // Viewing (vs. editing/resuming) always opens straight to the
          // Summary step, regardless of draft/completion state — matches the
          // legacy wizard forcing isPreviewPage whenever mode is
          // VIEW_BEHAVIOR (define-behavior.component.ts's getBehaviorById
          // subscribe), rather than resuming at the last-active field.
          this.currentIndex =
            this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
              ? this.steps.length - 1
              : this.wizardService.resolveResumeIndex(
                  this.steps,
                  response.result
                );
          if (this.isLastStep) {
            this.loadValidationStatus();
          }
        }
        this.cdr.markForCheck();
      });
  }

  // Drives the Summary page's pending-sections banner/red-text and the
  // Save & Close/Continue to Assessment button gating — see canProceed()
  // and hasPendingValidation(). Left null (nothing blocked) on error so the
  // feature degrades open rather than getting the user stuck.
  private loadValidationStatus(): void {
    const behaviorId = Number(this.caseParams.behaviorId);
    if (!behaviorId) {
      this.validationStatus = null;
      return;
    }
    this.behaviorService
      .getBehaviorValidationStatus(behaviorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.validationStatus = response?.result ?? null;
          this.cdr.markForCheck();
        },
        error: () => {
          this.validationStatus = null;
          this.cdr.markForCheck();
        },
      });
  }

  private patchExistingBehavior(detail: BehaviorDetailModel): void {
    // Pre-set selectedCategoryId so the behaviorCategoryId valueChanges
    // listener (wireFormReactions()) short-circuits when patchValue() below
    // sets it to this same value — otherwise it clears
    // caseBehaviorTypeDescriptions right after it's populated a few lines
    // down, leaving "Other" checked with no description textarea rendered.
    this.selectedCategoryId = detail.behaviorCategoryId;

    const caseBehaviorTypeIds = detail.caseBehaviorTypes.map(
      t => t.behaviorTypeId
    );
    const caseBehaviorEnvironmentIds = detail.caseBehaviorEnvironments.map(
      e => e.behaviorEnvironmentId
    );
    const caseBehaviorConsequenceIds = detail.caseBehaviorConsequences.map(
      c => c.behaviorConsequenceId
    );
    const caseBehaviorAntecedentIds = detail.caseBehaviorAntecedents.map(
      a => a.behaviorAntecedentId
    );
    const caseBehaviorSettingEventIds = detail.caseBehaviorSettingEvents.map(
      s => s.behaviorSettingEventId
    );

    detail.caseBehaviorTypeDescriptions.forEach(d =>
      this.behaviorTypeDescriptions.push(
        this.buildDescriptionGroup(
          'behaviorTypeId',
          d.behaviorTypeId,
          d.description
        )
      )
    );
    detail.caseBehaviorConsequenceDescriptions.forEach(d =>
      this.consequenceDescriptions.push(
        this.buildDescriptionGroup(
          'behaviorConsequenceId',
          d.behaviorConsequenceId,
          d.description
        )
      )
    );
    (detail.caseBehaviorAntecedentDescriptions || []).forEach(d =>
      this.antecedentDescriptions.push(
        this.buildDescriptionGroup(
          'behaviorAntecedentId',
          d.behaviorAntecedentId,
          d.description
        )
      )
    );

    this.form.patchValue({
      // caseId set in loadWizardData() from params.case?.id is a reasonable
      // default while this request is in flight, but the behavior detail
      // response is the authoritative source (same as the old wizard, which
      // relies on this.behaviorDetail.caseId via its formData spread rather
      // than the case param) — re-patch it here so an edit/view load can
      // never end up saving with a stale or missing caseId.
      caseId: detail.caseId,
      behaviorCategoryId: detail.behaviorCategoryId,
      operationalDefinition: detail.operationalDefinition,
      behaviourFrequencyId: detail.behaviourFrequencyId,
      behaviourFrequencyCount: detail.behaviourFrequencyCount,
      duration: detail.duration,
      behaviourIntensityId: detail.behaviourIntensityId,
      goals: detail.goals,
      draft: detail.draft,
      draftStep: detail.draftStep,
      draftStepType: detail.draftStepType,
      caseBehaviorTypeIds,
      caseBehaviorEnvironmentIds,
      caseBehaviorConsequenceIds,
      caseBehaviorAntecedentIds,
      caseBehaviorSettingEventIds,
      isDurationRequired: !!detail.duration,
    });

    (detail.caseBehaviorCustomQuestionAnswer || []).forEach(answer => {
      const index = this.questionAnswerIndex.get(answer.customQuestionId);
      if (index !== undefined) {
        this.customQuestionAnswers.at(index).patchValue({
          answer: answer.answer,
        });
      }
    });

    this.loadBehaviourSubTypes(detail.behaviorCategoryId);
    this.updateFrequencyValidators();
  }

  private buildDescriptionGroup(
    idKey: string,
    idValue: number,
    description: string
  ): FormGroup {
    return this.fb.group({
      [idKey]: [idValue],
      description: [
        description,
        [Validators.required, Validators.maxLength(4000)],
      ],
    });
  }

  private loadBehaviourSubTypes(categoryId: number): void {
    this.commonService
      .getBehaviourSubTypes(categoryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.behaviourSubCategoryList = response.result;
        this.cdr.markForCheck();
      });
  }

  private populateCustomQuestionAnswers(): void {
    this.questionAnswerIndex.clear();
    this.customQuestionAnswers.clear();
    const seen = new Set<number>();

    for (const step of this.steps) {
      for (const questionId of step.questionIds) {
        if (seen.has(questionId)) {
          continue;
        }
        seen.add(questionId);
        const gating = step.questionGating[questionId];
        const group = this.fb.group({
          customQuestionId: [questionId],
          answer: ['', gating?.isRequired ? [Validators.required] : []],
        });
        this.questionAnswerIndex.set(
          questionId,
          this.customQuestionAnswers.length
        );
        this.customQuestionAnswers.push(group);
      }
    }
  }

  // behaviourFrequencyCount/isDurationRequired/duration are governed entirely
  // by updateFrequencyValidators()/updateDurationValidators() below, not by
  // the template's per-step Required/Hard-Stop flag — see the comment there.
  private readonly frequencyGroupControlNames = new Set([
    'behaviourFrequencyCount',
    'isDurationRequired',
    'duration',
  ]);

  private applyStepValidators(): void {
    for (const step of this.steps) {
      const required = step.isRequired || step.isHardStop;
      for (const controlName of step.controlNames) {
        if (this.frequencyGroupControlNames.has(controlName)) {
          continue;
        }
        const control = this.form.get(controlName);
        if (!control || control instanceof FormArray) {
          continue;
        }
        const base = this.baseValidators[controlName] || [];
        control.setValidators(required ? [...base, Validators.required] : base);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  // Once the user picks a frequency at all, the rest of the Frequency group
  // becomes mandatory to complete — independent of whether the template
  // marked the Frequency step Required/Hard-Stop or left it Optional. This is
  // a structural completeness rule on the field group itself, not something a
  // template should be able to loosen (same as it already worked in the
  // fixed old wizard).
  private updateFrequencyValidators(): void {
    const hasStartedFrequency = !!this.form.get('behaviourFrequencyId')?.value;
    const groupRequired = this.frequencyStepRequired || hasStartedFrequency;

    const countControl = this.form.get('behaviourFrequencyCount');
    const countBase = this.baseValidators['behaviourFrequencyCount'] || [];
    countControl?.setValidators(
      groupRequired ? [...countBase, Validators.required] : countBase
    );
    countControl?.updateValueAndValidity({ emitEvent: false });

    const durationRequiredControl = this.form.get('isDurationRequired');
    durationRequiredControl?.setValidators(
      groupRequired ? [Validators.required] : []
    );
    durationRequiredControl?.updateValueAndValidity({ emitEvent: false });

    this.updateDurationValidators();
  }

  // duration's requirement is driven purely by the "Minutes" vs.
  // "instantaneous/doesn't matter" choice — not by the template flag, and not
  // by whether the rest of the group was force-required by
  // updateFrequencyValidators() above (you still can't require a duration
  // value when the user said duration doesn't apply).
  private updateDurationValidators(): void {
    const durationControl = this.form.get('duration');
    const isDurationRequired = !!this.form.get('isDurationRequired')?.value;
    durationControl?.setValidators([
      Validators.pattern('^[0-9]*\\.?[0-9]*$'),
      Validators.maxLength(6),
      ...(isDurationRequired ? [Validators.required] : []),
    ]);
    durationControl?.updateValueAndValidity({ emitEvent: false });
  }

  get behaviorTypeDescriptions(): FormArray {
    return this.form.controls['caseBehaviorTypeDescriptions'] as FormArray;
  }

  get antecedentDescriptions(): FormArray {
    return this.form.controls['caseBehaviorAntecedentDescription'] as FormArray;
  }

  get consequenceDescriptions(): FormArray {
    return this.form.controls[
      'caseBehaviorConsequenceDescription'
    ] as FormArray;
  }

  get customQuestionAnswers(): FormArray {
    return this.form.controls['caseBehaviorCustomQuestionAnswer'] as FormArray;
  }

  get behaviorTypeDescriptionControls(): FormGroup[] {
    return this.behaviorTypeDescriptions.controls as FormGroup[];
  }

  get antecedentDescriptionControls(): FormGroup[] {
    return this.antecedentDescriptions.controls as FormGroup[];
  }

  get consequenceDescriptionControls(): FormGroup[] {
    return this.consequenceDescriptions.controls as FormGroup[];
  }

  get currentStep(): WizardStep | null {
    return this.steps[this.currentIndex] ?? null;
  }

  get isLastStep(): boolean {
    return this.currentIndex === this.steps.length - 1;
  }

  get isActionInProgress(): boolean {
    return this.isSaving;
  }

  // Purely template-driven: skips Assessment/Strategy if the template excludes them, landing on
  // whichever of Assessment -> Strategy -> Data is next; null (no continue button) once nothing's
  // left. Completion state of any tile plays no part in this decision.
  get nextContinueTile(): BipTileSection | null {
    return this.bipTemplateGatingService.resolveNextIncludedTile(
      this.bipTemplate,
      BipTileSection.Details
    );
  }

  answerControlAt(questionId: number): FormGroup | null {
    const index = this.questionAnswerIndex.get(questionId);
    return index !== undefined
      ? (this.customQuestionAnswers.at(index) as FormGroup)
      : null;
  }

  questionLabel(questionId: number): string {
    return (
      this.templateCustomQuestions.find(question => question.id === questionId)
        ?.customQuestion ?? ''
    );
  }

  getCategoryNameById(id: number): string {
    return (
      this.behaviorCategoryList.find(category => category.id === id)?.name ?? ''
    );
  }

  // Arrow-function fields (not methods) so the bound reference passed down
  // via @Input() to the OnPush step children stays stable across change
  // detection cycles instead of being recreated by a template `.bind(this)`.
  getBehaviorTypeDescriptionTitle = (id: number): string => {
    return (
      this.behaviourSubCategoryList.find(item => item.id === id)?.name ?? ''
    );
  };

  getBehaviorAntecedentTitle = (id: number): string => {
    return (
      this.behaviourAntecedentList.find(item => item.id === id)?.name ?? ''
    );
  };

  getBehaviorConsequenceTitle = (id: number): string => {
    return (
      this.behaviorConsequenceList.find(item => item.id === id)?.name ?? ''
    );
  };

  trackById(_index: number, item: { id: number }): number {
    return item.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByGoal(_index: number, item: { goal: string }): string {
    return item.goal;
  }

  isHelpTextTruncated(text: string | null | undefined): boolean {
    return (text ?? '').trim().length > 90;
  }

  onCheckboxChange = (
    controlName: string,
    checked: boolean,
    value: number
  ): void => {
    const control = this.form.get(controlName);
    control?.markAsTouched();
    const selectedOptions: number[] = control?.value || [];
    if (checked) {
      selectedOptions.push(value);
    } else {
      const index = selectedOptions.indexOf(value);
      if (index > -1) {
        selectedOptions.splice(index, 1);
      }
    }
    control?.setValue(selectedOptions);
  };

  onBehaviorTypeChange = (
    checked: boolean,
    category: BehaviourListModel
  ): void => {
    if (checked && category.isOther) {
      this.behaviorTypeDescriptions.push(
        this.buildDescriptionGroup('behaviorTypeId', category.id, '')
      );
    } else if (category.isOther && !checked) {
      const index = this.behaviorTypeDescriptions.value.findIndex(
        (v: caseBehaviorTypeDescriptions) => v.behaviorTypeId === category.id
      );
      if (index !== -1) {
        this.behaviorTypeDescriptions.removeAt(index);
      }
    }
  };

  onAntecedentChange = (
    checked: boolean,
    category: BehaviourListModel
  ): void => {
    if (checked && (category.name === 'Other' || category.id === 20)) {
      this.antecedentDescriptions.push(
        this.buildDescriptionGroup('behaviorAntecedentId', category.id, '')
      );
    } else if (!checked && (category.name === 'Other' || category.id === 20)) {
      const index = this.antecedentDescriptions.value.findIndex(
        (v: { behaviorAntecedentId: number }) =>
          v.behaviorAntecedentId === category.id
      );
      if (index !== -1) {
        this.antecedentDescriptions.removeAt(index);
      }
    }
  };

  onConsequenceChange = (
    checked: boolean,
    category: BehaviourListModel
  ): void => {
    if (checked && category.isOther) {
      this.consequenceDescriptions.push(
        this.buildDescriptionGroup('behaviorConsequenceId', category.id, '')
      );
    } else if (category.isOther && !checked) {
      const index = this.consequenceDescriptions.value.findIndex(
        (v: { behaviorConsequenceId: number }) =>
          v.behaviorConsequenceId === category.id
      );
      if (index !== -1) {
        this.consequenceDescriptions.removeAt(index);
      }
    }
  };

  onControlInputChange = (
    event: Event,
    control: AbstractControl | null
  ): void => {
    const input = event.target as HTMLTextAreaElement;
    if (/^\s+$/.test(input.value)) {
      input.value = '';
      control?.setValue('');
    }
  };

  getBehaviorGoalLabel = (categoryId: number): string => {
    const category = this.behaviorCategoryList.find(
      cat => cat.id === categoryId
    );
    if (!category) {
      return 'Add details';
    }
    const example = this.behaviorGoalExamples.behavioral_goals.find(
      goal =>
        goal.goal.toLocaleLowerCase() === category.name.toLocaleLowerCase()
    );
    return example ? example.description : 'Add details';
  };

  // A disabled control always reports .valid === true, so gating on a
  // not-yet-enabled field (e.g. duration before "Minutes" is chosen) requires
  // manually invoking its validator instead of trusting .valid.
  checkValidation(control: AbstractControl | null): boolean {
    if (!control) {
      return false;
    }
    if (control.disabled) {
      const errors = control.validator?.(control);
      return !errors?.['required'];
    }
    return control.valid;
  }

  isStepSatisfied(step: WizardStep): boolean {
    if (step.questionIds.length > 0) {
      return step.questionIds.every(questionId => {
        const group = this.answerControlAt(questionId);
        return group ? this.checkValidation(group.get('answer')) : true;
      });
    }
    return step.controlNames.every(controlName => {
      const control = this.form.get(controlName);
      if (!control) {
        return true;
      }
      if (control instanceof FormArray) {
        return this.isDescriptionArraySatisfied(control);
      }
      return this.checkValidation(control);
    });
  }

  // Required (non-Hard-Stop) steps are skippable while stepping through the
  // wizard — only a Hard-Stop step actually blocks Save & Next/Save &
  // Preview/Save & Close/Continue. A skipped Required step still shows up on
  // Summary via outstandingRequiredSteps()/hasPendingValidation, which is
  // unaffected by this — this only gates per-step navigation.
  //
  // A CustomQuestionBundle step can mix Hard-Stop and plain-Required
  // questions under one step — step.isHardStop is true if *any* question in
  // it is Hard-Stop, but that must not block on the Required-only questions
  // too, so question-based steps are checked per-question here instead of
  // via the step-level aggregate flag.
  isStepHardBlocked(step: WizardStep): boolean {
    if (step.questionIds.length > 0) {
      return step.questionIds.some(questionId => {
        if (!step.questionGating[questionId]?.isHardStop) {
          return false;
        }
        const group = this.answerControlAt(questionId);
        return group ? !this.checkValidation(group.get('answer')) : false;
      });
    }
    return step.isHardStop && !this.isStepSatisfied(step);
  }

  // "Other" description groups (Behavior Types/Antecedents/Consequences) are
  // required the moment "Other" is checked, regardless of the step's
  // template Required/Hard-Stop flag — see buildDescriptionGroup(). Each
  // entry's own control-level validator already enforces that; this just
  // makes step-level gating (canProceed/Hard-Stop blocking) actually respect
  // it instead of treating every FormArray as automatically satisfied.
  private isDescriptionArraySatisfied(formArray: FormArray): boolean {
    return formArray.controls.every(control => {
      if (control instanceof FormGroup) {
        return Object.values(control.controls).every(child =>
          this.checkValidation(child)
        );
      }
      return this.checkValidation(control);
    });
  }

  private markStepTouched(step: WizardStep): void {
    if (step.questionIds.length > 0) {
      step.questionIds.forEach(questionId => {
        this.answerControlAt(questionId)?.get('answer')?.markAsTouched();
      });
      return;
    }
    step.controlNames.forEach(controlName => {
      this.form.get(controlName)?.markAsTouched();
    });
  }

  outstandingRequiredSteps(): WizardStep[] {
    return this.steps.filter(
      step => step.isRequired && !this.isStepSatisfied(step)
    );
  }

  // Drives [disabled] on Save & Next / Save & Close / Continue. Summary
  // completion (whether any Required/Hard-Stop section anywhere in the
  // wizard is still outstanding) is no longer judged client-side — the
  // backend's validation API (see hasPendingValidation()) is the source of
  // truth there, plus the payload's own business-validation error handling
  // in save()'s error handler as a final backstop. This getter only still
  // gates on whether the *current* step is Hard-Stop-blocked (see
  // isStepHardBlocked()) — a plain Required step is skippable, which is a
  // no-op on Summary since it's never Hard-Stop.
  get canProceed(): boolean {
    return !!this.currentStep && !this.isStepHardBlocked(this.currentStep);
  }

  // Backend-sourced completion signal for Summary's Save & Close/Continue to
  // Assessment buttons (see loadValidationStatus()) — blocks on either
  // pending Hard-Stop or pending Required items. Defaults to false (not
  // blocked) while validationStatus is null — i.e. before the API responds
  // or on error — so the feature degrades open rather than getting the user
  // stuck.
  get hasPendingValidation(): boolean {
    return !!(
      this.validationStatus?.hasPendingHardStop ||
      this.validationStatus?.hasPendingRequired
    );
  }

  get pendingHardStopNames(): string[] {
    return (this.validationStatus?.pendingHardStopItems ?? []).map(
      item => item.name
    );
  }

  get pendingRequiredNames(): string[] {
    return (this.validationStatus?.pendingRequiredItems ?? []).map(
      item => item.name
    );
  }

  onNext(): void {
    if (this.isActionInProgress || !this.currentStep) {
      return;
    }
    const step = this.currentStep;
    if (this.isStepHardBlocked(step)) {
      this.markStepTouched(step);
      this.cdr.markForCheck();
      return;
    }
    this.save(false, () => {
      if (!this.isLastStep) {
        this.currentIndex++;
        if (this.isLastStep) {
          this.loadValidationStatus();
        }
      }
      this.cdr.markForCheck();
    });
  }

  // Ports define-behavior.component.ts's onSave(): once the behavior is
  // already fully complete (!isBehaviorDraft), editing any earlier step and
  // saving should jump straight back to Summary instead of stepping through
  // every remaining step one Save & Next at a time.
  onSaveAndPreview(): void {
    if (this.isActionInProgress || !this.currentStep) {
      return;
    }
    const step = this.currentStep;
    if (this.isStepHardBlocked(step)) {
      this.markStepTouched(step);
      this.cdr.markForCheck();
      return;
    }
    this.save(false, () => {
      this.currentIndex = this.steps.length - 1;
      this.loadValidationStatus();
      this.cdr.markForCheck();
    });
  }

  onPrevious(): void {
    if (this.currentIndex > 0) {
      if (this.isLastStep) {
        this.cameFromSummary = true;
      }
      this.currentIndex--;
    }
  }

  // Invoked by the Summary step's edit buttons — jumps back to any earlier
  // step for editing. Unlike the legacy wizard's redirectToStep(), the form
  // here is never disabled on the summary step, so there's no re-enable step
  // — flipping out of VIEW_BEHAVIOR is the only state change needed.
  redirectToStep(stepIndex: number): void {
    if (stepIndex < 0 || stepIndex >= this.steps.length) {
      return;
    }
    if (this.caseParams.mode === InsightMode.VIEW_BEHAVIOR) {
      this.caseParams.mode = InsightMode.EDIT_BEHAVIOR;
    }
    this.cameFromSummary = true;
    this.currentIndex = stepIndex;
    this.cdr.markForCheck();
  }

  onSaveForLater(): void {
    if (this.isActionInProgress) {
      return;
    }
    this.save(false, result => this.closeDrawer(true, result));
  }

  onFinish(): void {
    if (this.isActionInProgress || !this.currentStep) {
      return;
    }
    const step = this.currentStep;
    if (this.isStepHardBlocked(step)) {
      this.markStepTouched(step);
      this.cdr.markForCheck();
      return;
    }
    this.isContinueProcessing = false;
    this.save(true);
  }

  onContinue(): void {
    if (this.isActionInProgress || !this.currentStep) {
      return;
    }
    const step = this.currentStep;
    if (this.isStepHardBlocked(step)) {
      this.markStepTouched(step);
      this.cdr.markForCheck();
      return;
    }
    this.isContinueProcessing = true;
    this.save(true);
  }

  private save(
    isFinalAttempt: boolean,
    onSuccess?: (result: BehaviorModel | null) => void
  ): void {
    if (!this.currentStep) {
      return;
    }

    // draft stays true until every Required/Hard-Stop step (isRequired is
    // already true for Hard-Stop steps too — see toStep()) is filled and
    // valid; recomputed on every save, not just the final one, so the
    // behavior's draft status always reflects current completion state.
    const outstanding = this.outstandingRequiredSteps();

    // From Summary (Save & Close / Continue to Assessment / Save for Later),
    // draftStep/draftStepType stop tracking "the step the user is currently
    // on" — Summary has no controls of its own — and instead point at the
    // first still-incomplete Required/Hard-Stop step anywhere in the wizard,
    // so resuming lands the user back on the actual gap. Once nothing is
    // outstanding the behavior is fully complete, so these (and, in edit
    // mode, applicationObjectId/questionId derived from them below) are
    // cleared entirely rather than pointing at Summary's own sentinel refId.
    // Save & Next earlier in the wizard is untouched — it keeps reporting the
    // step just completed, as before.
    // Also tracked for the applicationObjectId/questionId/
    // submitAllCustomQuestions derivation further below — the step whose
    // position draftStep/draftStepType above actually points at.
    const activeStep: WizardStep | null = this.isLastStep
      ? (outstanding[0] ?? null)
      : this.currentStep;

    let draftStep: number | null;
    let draftStepType: DraftStepType | null;

    if (activeStep) {
      if (this.isLastStep) {
        draftStep = activeStep.refId;
        draftStepType = activeStep.draftStepType;
      } else {
        const draftPosition = this.wizardService.toDraftPosition(activeStep);
        draftStep = draftPosition.draftStep;
        draftStepType = draftPosition.draftStepType;
      }
    } else {
      draftStep = null;
      draftStepType = null;
    }

    this.form.patchValue({ draftStep, draftStepType }, { emitEvent: false });

    this.form.patchValue(
      { draft: outstanding.length > 0 },
      { emitEvent: false }
    );

    // Scope the diff to the step being left while navigating (matches the
    // per-step audit scoping in the old wizard); on final submit, diff the
    // whole form so nothing from earlier steps is missed.
    const stepFields = isFinalAttempt
      ? undefined
      : this.currentStep.controlNames;
    const formData = this.form.getRawValue() as Record<string, unknown>;
    const modifiedFields = this.buildModifiedFields(formData, stepFields);

    let modifiedFieldsToSend: AuditFieldChange[] = [];
    if (modifiedFields.length > 0) {
      modifiedFieldsToSend = modifiedFields;
    } else if (isFinalAttempt && this.lastModifiedFields.length > 0) {
      modifiedFieldsToSend = this.lastModifiedFields;
    }
    if (modifiedFields.length > 0) {
      this.lastModifiedFields = modifiedFields;
    }

    const isEditMode = this.isEditOrViewMode();
    const isApplicationObjectStep =
      draftStepType === DraftStepType.ApplicationObject;
    // Guards below key off activeStep (the step draftStep/draftStepType
    // above actually point at — either currentStep, or, from Summary, the
    // first outstanding step), not currentStep directly, since on Summary
    // currentStep is always the Summary step itself.
    // Only NativeField steps carry a real lookup-id applicationObjectId.
    // BehaviorCategory (refId 1) and Summary (refId 2) are ApplicationObject-
    // typed sentinels (see buildCategoryStep()/buildSummaryStep()) and must
    // never be sent as a real applicationObjectId.
    const isNativeFieldStep = activeStep?.kind === WizardStepKind.NativeField;
    // CustomQuestionBundle's refId is only a resume sentinel (the bundle's
    // first questionIds entry — see buildBundleStepFromList()), not a single
    // real question being answered, so it must never be sent as questionId —
    // submitAllCustomQuestions is sent instead so the backend knows every
    // question in the bundle is being submitted together.
    const isCustomQuestionBundleStep =
      activeStep?.kind === WizardStepKind.CustomQuestionBundle;

    const payload = {
      ...formData,
      ...(modifiedFieldsToSend.length > 0
        ? { _modifiedFields: modifiedFieldsToSend }
        : {}),
      ...(isEditMode
        ? {
            applicationObjectId:
              isApplicationObjectStep && isNativeFieldStep ? draftStep : null,
            questionId:
              isApplicationObjectStep || isCustomQuestionBundleStep
                ? null
                : draftStep,
            submitAllCustomQuestions: isCustomQuestionBundleStep,
          }
        : {}),
    } as BehaviorModel;

    if (!environment.production) {
      console.log('[DefineBehaviorWizard] payload', payload);
    }

    this.isSaving = true;
    const request$ = isEditMode
      ? this.behaviorService.editBehavior(
          payload,
          Number(this.caseParams.behaviorId)
        )
      : this.behaviorService.createBehavior(payload);

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          if (response?.result?.id) {
            this.caseParams.behaviorId = response.result.id;
            this.caseParams.mode = InsightMode.EDIT_BEHAVIOR;
          }
          if (isFinalAttempt) {
            this.lastModifiedFields = [];
            this.closeDrawer(true, response?.result ?? null);
          } else {
            onSuccess?.(response?.result ?? null);
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors?.map(
            err => err.description || err.detailedMessage
          ) || [message];
          this.commonService.notify('error', errorMessages, message);
          // Refresh the pending-sections banner/badges with the backend's
          // latest state after a failed submit from Summary.
          if (this.isLastStep) {
            this.loadValidationStatus();
          }
        },
      });
  }

  private isEditOrViewMode(): boolean {
    return (
      this.caseParams.mode === InsightMode.EDIT_BEHAVIOR ||
      this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
    );
  }

  // Ported from define-behavior.component.ts's buildBehaviorModifiedFields +
  // its helpers, adapted to this wizard's step model (stepFields comes from
  // WizardStep.controlNames instead of pageTitles[currentPage-1].controls).
  private buildModifiedFields(
    formData: Record<string, unknown>,
    stepFields?: string[]
  ): AuditFieldChange[] {
    if (!this.isTrackableMode()) {
      return [];
    }

    const isCreateMode = this.caseParams.mode === InsightMode.ADD_BEHAVIOR;
    const baselineBehaviorDetail = this.auditBaselineBehaviorDetail;
    const hasPersistedBaseline = !isCreateMode && !!baselineBehaviorDetail;
    const labelMap: Record<string, string> = {
      behaviorCategory: 'Behavior Category',
      operationalDefinition: 'Operational Definition',
      behaviorTypes: 'Behavior Types',
      caseBehaviorTypeDescriptions: 'Behavior Type Descriptions',
      behaviorFrequency: 'Frequency',
      behaviourFrequencyCount: 'Frequency Count',
      isDurationRequired: 'Duration Required',
      duration: 'Duration',
      behaviorIntensity: 'Intensity',
      goals: 'Goals',
      draft: 'Draft Status',
      draftStep: 'Draft Step',
      behaviorAntecedents: 'Antecedents',
      caseBehaviorAntecedentDescriptions: 'Antecedent Descriptions',
      behaviorConsequences: 'Consequences',
      caseBehaviorConsequenceDescriptions: 'Consequence Descriptions',
      behaviorEnvironments: 'Environments',
      behaviorSettingEvents: 'Setting Events',
      customQuestions: 'Custom Questions',
      caseBehaviorCustomQuestionAnswer: 'Custom Question Answers',
    };

    const sensitiveFields: string[] = [];
    const idFields = [
      'behaviorCategoryId',
      'caseBehaviorTypeIds',
      'behaviourFrequencyId',
      'behaviourIntensityId',
      'caseBehaviorAntecedentIds',
      'caseBehaviorConsequenceIds',
      'caseBehaviorEnvironmentIds',
      'caseBehaviorSettingEventIds',
      'caseBehaviorCustomQuestionAnswer',
    ];
    const idDerivedFieldMap: Record<string, string> = {
      behaviorCategoryId: 'behaviorCategory',
      caseBehaviorTypeIds: 'behaviorTypes',
      behaviourFrequencyId: 'behaviorFrequency',
      behaviourIntensityId: 'behaviorIntensity',
      caseBehaviorAntecedentIds: 'behaviorAntecedents',
      caseBehaviorConsequenceIds: 'behaviorConsequences',
      caseBehaviorEnvironmentIds: 'behaviorEnvironments',
      caseBehaviorSettingEventIds: 'behaviorSettingEvents',
      caseBehaviorCustomQuestionAnswer: 'customQuestions',
    };
    const stepFieldMap: Record<string, string> = {
      ...idDerivedFieldMap,
      caseBehaviorAntecedentDescription: 'caseBehaviorAntecedentDescriptions',
      caseBehaviorConsequenceDescription: 'caseBehaviorConsequenceDescriptions',
    };
    const excludeFields = [
      'id',
      'caseId',
      ...idFields,
      'caseBehaviorConsequenceDescription',
      'caseBehaviorAntecedentDescription',
    ];

    const allOriginal: Record<string, unknown> = hasPersistedBaseline
      ? {
          behaviorCategoryId:
            baselineBehaviorDetail?.behaviorCategoryId || null,
          operationalDefinition:
            baselineBehaviorDetail?.operationalDefinition || '',
          caseBehaviorTypeIds: this.normalizeIdArray(
            baselineBehaviorDetail?.caseBehaviorTypes?.map(
              (x: { behaviorTypeId: number }) => x.behaviorTypeId
            ) || []
          ),
          caseBehaviorTypeDescriptions: this.normalizeDescriptionArray(
            baselineBehaviorDetail?.caseBehaviorTypeDescriptions || []
          ),
          behaviourFrequencyId:
            baselineBehaviorDetail?.behaviourFrequencyId ?? null,
          behaviourFrequencyCount:
            baselineBehaviorDetail?.behaviourFrequencyCount ?? null,
          isDurationRequired: baselineBehaviorDetail?.duration
            ? !!baselineBehaviorDetail.duration
            : null,
          duration: baselineBehaviorDetail?.duration ?? null,
          behaviourIntensityId:
            baselineBehaviorDetail?.behaviourIntensityId ?? null,
          goals: baselineBehaviorDetail?.goals || '',
          draft: baselineBehaviorDetail?.draft ?? true,
          draftStep: baselineBehaviorDetail?.draftStep || 0,
          caseBehaviorAntecedentIds: this.normalizeIdArray(
            baselineBehaviorDetail?.caseBehaviorAntecedents?.map(
              (x: { behaviorAntecedentId: number }) => x.behaviorAntecedentId
            ) || []
          ),
          caseBehaviorAntecedentDescriptions: this.normalizeDescriptionArray(
            baselineBehaviorDetail?.caseBehaviorAntecedentDescriptions || []
          ),
          caseBehaviorConsequenceIds: this.normalizeIdArray(
            baselineBehaviorDetail?.caseBehaviorConsequences?.map(
              (x: { behaviorConsequenceId: number }) => x.behaviorConsequenceId
            ) || []
          ),
          caseBehaviorConsequenceDescriptions: this.normalizeDescriptionArray(
            baselineBehaviorDetail?.caseBehaviorConsequenceDescriptions || []
          ),
          caseBehaviorEnvironmentIds: this.normalizeIdArray(
            baselineBehaviorDetail?.caseBehaviorEnvironments?.map(
              (x: { behaviorEnvironmentId: number }) => x.behaviorEnvironmentId
            ) || []
          ),
          caseBehaviorSettingEventIds: this.normalizeIdArray(
            baselineBehaviorDetail?.caseBehaviorSettingEvents?.map(
              (x: { behaviorSettingEventId: number }) =>
                x.behaviorSettingEventId
            ) || []
          ),
          caseBehaviorCustomQuestionAnswer:
            baselineBehaviorDetail?.caseBehaviorCustomQuestionAnswer || [],
        }
      : {};

    const allFormData: Record<string, unknown> = {
      ...formData,
      caseBehaviorTypeIds: this.normalizeIdArray(
        formData['caseBehaviorTypeIds']
      ),
      caseBehaviorTypeDescriptions: this.normalizeDescriptionArray(
        formData['caseBehaviorTypeDescriptions']
      ),
      caseBehaviorAntecedentIds: this.normalizeIdArray(
        formData['caseBehaviorAntecedentIds']
      ),
      caseBehaviorAntecedentDescriptions: this.normalizeDescriptionArray(
        formData['caseBehaviorAntecedentDescription']
      ),
      caseBehaviorConsequenceIds: this.normalizeIdArray(
        formData['caseBehaviorConsequenceIds']
      ),
      caseBehaviorConsequenceDescriptions: this.normalizeDescriptionArray(
        formData['caseBehaviorConsequenceDescription']
      ),
      caseBehaviorEnvironmentIds: this.normalizeIdArray(
        formData['caseBehaviorEnvironmentIds']
      ),
      caseBehaviorSettingEventIds: this.normalizeIdArray(
        formData['caseBehaviorSettingEventIds']
      ),
      caseBehaviorCustomQuestionAnswer:
        formData['caseBehaviorCustomQuestionAnswer'] || [],
      isDurationRequired: !!formData['isDurationRequired'],
    };

    Object.entries(idDerivedFieldMap).forEach(([idField, derivedField]) => {
      if (!isCreateMode) {
        allOriginal[derivedField] = this.resolveAuditFieldName(
          idField,
          allOriginal[idField]
        );
      }
      allFormData[derivedField] = this.resolveAuditFieldName(
        idField,
        allFormData[idField]
      );
    });

    if (!isCreateMode) {
      allOriginal['customQuestions'] = this.normalizeCustomQuestionAnswers(
        allOriginal['caseBehaviorCustomQuestionAnswer']
      );
    }
    allFormData['customQuestions'] = this.normalizeCustomQuestionAnswers(
      allFormData['caseBehaviorCustomQuestionAnswer']
    );

    let original: Record<string, unknown> = {};
    let filteredFormData: Record<string, unknown> = {};

    if (stepFields && stepFields.length > 0) {
      const normalizedStepFields = Array.from(
        new Set(stepFields.map(field => stepFieldMap[field] || field))
      );

      normalizedStepFields.forEach(field => {
        original[field] = allOriginal[field];
        filteredFormData[field] = allFormData[field];
      });
    } else {
      original = allOriginal;
      filteredFormData = allFormData;
    }

    const rawChanges = this.auditContextService.buildFieldChanges(
      original,
      filteredFormData,
      { sensitiveFields, labelMap, excludeFields }
    );

    const normalizedChanges = rawChanges
      .filter(change => change.oldValue !== change.newValue)
      .filter(change => {
        if (!this.isDescriptionField(change.field || '')) {
          return true;
        }

        return !(
          this.isEmptyAuditValue(change.oldValue) &&
          this.isEmptyAuditValue(change.newValue)
        );
      })
      .map(change => ({
        ...change,
        oldValue: this.convertBooleanToYesNo(change.oldValue),
        newValue: this.convertBooleanToYesNo(change.newValue),
      }));

    const shouldTrackCustomQuestions =
      !stepFields || stepFields.includes('caseBehaviorCustomQuestionAnswer');

    if (shouldTrackCustomQuestions) {
      const oldCustomQuestions = this.toAuditAnswerString(
        original['customQuestions']
      );
      const newCustomQuestions = this.toAuditAnswerString(
        filteredFormData['customQuestions']
      );

      const hasCustomQuestionsChange = normalizedChanges.some(
        change => change.field === 'customQuestions'
      );

      if (
        !hasCustomQuestionsChange &&
        oldCustomQuestions !== newCustomQuestions
      ) {
        normalizedChanges.push({
          field: 'customQuestions',
          fieldLabel: labelMap['customQuestions'] || 'customQuestions',
          oldValue: oldCustomQuestions,
          newValue: newCustomQuestions,
          isSensitive: false,
        });
      }
    }

    return normalizedChanges;
  }

  private isTrackableMode(): boolean {
    return (
      this.caseParams.mode === InsightMode.ADD_BEHAVIOR ||
      this.caseParams.mode === InsightMode.EDIT_BEHAVIOR ||
      this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
    );
  }

  private convertBooleanToYesNo(value: unknown): string {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase().trim();
      if (lowerValue === 'true') {
        return 'Yes';
      }
      if (lowerValue === 'false') {
        return 'No';
      }
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
    return '';
  }

  private resolveAuditFieldName(field: string, value: unknown): unknown {
    const ids = this.normalizeIdArray(value);

    const mapIdsToNames = (source: { id: number; name?: string }[]): string[] =>
      ids
        .map(id => source.find(item => item.id === Number(id))?.name || `${id}`)
        .filter(name => !!name);

    switch (field) {
      case 'behaviorCategoryId':
        return (
          this.behaviorCategoryList.find(q => q.id === value)?.name || value
        );
      case 'caseBehaviorTypeIds':
        return mapIdsToNames(this.behaviourSubCategoryList);
      case 'caseBehaviorEnvironmentIds':
        return mapIdsToNames(this.behaviourEnvironmentList);
      case 'caseBehaviorSettingEventIds':
        return mapIdsToNames(this.behaviourSettingEventList);
      case 'caseBehaviorAntecedentIds':
        return mapIdsToNames(this.behaviourAntecedentList);
      case 'caseBehaviorConsequenceIds':
        return mapIdsToNames(this.behaviorConsequenceList);
      case 'behaviourFrequencyId':
        return (
          this.behaviourFrequencyList.find(q => q.id === value)?.name || value
        );
      case 'behaviourIntensityId':
        return (
          this.behaviourIntensityList.find(q => q.id === value)?.name || value
        );
      default:
        return value;
    }
  }

  private normalizeIdArray(value: unknown): (number | string)[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map(item => {
        if (item === null || item === undefined) {
          return null;
        }

        if (typeof item === 'number' || typeof item === 'string') {
          return item;
        }

        if (typeof item === 'object') {
          const typedItem = item as Record<string, unknown>;
          return (
            typedItem['id'] ??
            typedItem['behaviorTypeId'] ??
            typedItem['behaviorAntecedentId'] ??
            typedItem['behaviorConsequenceId'] ??
            typedItem['behaviorEnvironmentId'] ??
            typedItem['behaviorSettingEventId'] ??
            null
          );
        }

        return null;
      })
      .filter((item): item is number | string => item !== null)
      .sort((a, b) => `${a}`.localeCompare(`${b}`));
  }

  private normalizeDescriptionArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map(item => {
        if (typeof item === 'string') {
          return item.trim();
        }

        if (item && typeof item === 'object') {
          const description = (item as Record<string, unknown>)['description'];
          return typeof description === 'string' ? description.trim() : '';
        }

        return '';
      })
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }

  private normalizeCustomQuestionAnswers(
    value: unknown
  ): Record<string, string> {
    if (!Array.isArray(value)) {
      return {};
    }

    const normalized: Record<string, string> = {};

    value.forEach(item => {
      if (!item || typeof item !== 'object') {
        return;
      }

      const typedItem = item as Record<string, unknown>;
      const questionId = typedItem['customQuestionId'];
      const answer = typedItem['answer'];
      const questionFromLookup = this.templateCustomQuestions.find(
        q => q.id === Number(questionId)
      )?.customQuestion;
      const questionFromPayload =
        (typedItem['customQuestion'] as string | undefined) ||
        (typedItem['question'] as string | undefined) ||
        (typedItem['questionText'] as string | undefined);
      const question = questionFromLookup || questionFromPayload;
      const questionIdText =
        typeof questionId === 'string' || typeof questionId === 'number'
          ? String(questionId)
          : '';
      const fallbackQuestionKey = questionIdText
        ? `Question ID ${questionIdText}`
        : '';
      const questionKey = question || fallbackQuestionKey;

      if (!questionKey) {
        return;
      }

      normalized[questionKey] = this.toAuditAnswerString(answer);
    });

    return Object.keys(normalized)
      .sort((a, b) => a.localeCompare(b))
      .reduce<Record<string, string>>((acc, key) => {
        acc[key] = normalized[key];
        return acc;
      }, {});
  }

  private isDescriptionField(field: string): boolean {
    return [
      'caseBehaviorTypeDescriptions',
      'caseBehaviorAntecedentDescriptions',
      'caseBehaviorConsequenceDescriptions',
    ].includes(field);
  }

  private isEmptyAuditValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value !== 'string') {
      return false;
    }

    const normalized = value.trim();
    return (
      normalized === '' ||
      normalized === '[]' ||
      normalized === '{}' ||
      normalized === 'null'
    );
  }

  private toAuditAnswerString(value: unknown): string {
    if (value === undefined || value === null) {
      return '';
    }

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return String(value);
    }

    return JSON.stringify(value);
  }

  closeDrawer(
    isRecordChange = false,
    result: BehaviorModel | null = null
  ): void {
    if (this.isContinueProcessing) {
      const behaviorResult: CaseBehaviour = {
        id: result?.id || 0,
        behaviorCategoryId: result?.behaviorCategoryId || 0,
        behaviorCategoryName:
          this.behaviorCategoryList.find(
            item => item.id === result?.behaviorCategoryId
          )?.name || '',
        behaviorCategoryTypes:
          this.behaviorDetail?.caseBehaviorTypes.map(item => ({
            behaviorTypeId: item.behaviorTypeId,
            behaviorTypeName: item.behaviorTypeName,
            id: 0,
            caseBehaviorId: result?.id || 0,
          })) || [],
        behaviorFunction: [],
        behaviorAssessmentGraph: [],
        statusId: 1,
        resourceAddedDate: new Date().toISOString(),
        isCorrectionNeeded: false,
        modifiedDate: new Date().toISOString(),
        dataEnteredDate: new Date().toISOString(),
        operationalDefinition: result?.operationalDefinition || '',
        appliedStrategyCount: 0,
      };
      switch (this.nextContinueTile) {
        case BipTileSection.Strategy:
          this.openManageBehaviorStrategiesDrawer(behaviorResult);
          break;
        case BipTileSection.Data:
          this.openDataDrawer(behaviorResult);
          break;
        default:
          this.openAssessmentFbaDrawer(behaviorResult, true);
      }
      return;
    }
    const drawer = this.drawerService.getDrawerRef(
      DRAWER_IDS.DEFINE_BEHAVIOR_V2
    );
    drawer?.close({
      isRecordChange,
      isContinueProcessing: this.isContinueProcessing,
      result,
    });
  }

  openAssessmentFbaDrawer(behavior: CaseBehaviour, isDetailPage = false): void {
    this.drawerService
      .openDrawer(
        FbaAssessmentComponent,
        this.drawerFBATitleTpl as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          caseDetail: this.caseParams.case,
          behavior,
          chartImage: null,
          mode: isDetailPage ? InsightMode.EDIT : InsightMode.VIEW,
        },
        DRAWER_IDS.FBA
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.drawerService.closeDrawer(DRAWER_IDS.DEFINE_BEHAVIOR_V2);
      });
  }

  closeAssessmentFbaDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.FBA);
  }

  openManageBehaviorStrategiesDrawer(behavior: CaseBehaviour): void {
    this.drawerService
      .openDrawer(
        ManageBehaviorStrategiesComponent,
        this.drawerTitleBehaviorStrategies as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          case: this.caseParams.case,
          behavior,
          mode: InsightMode.VIEW,
          orgId: this.caseParams.case?.student?.organizationId,
          isContinueProcessing: false,
        },
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.drawerService.closeDrawer(DRAWER_IDS.DEFINE_BEHAVIOR_V2);
      });
  }

  closeManageBehaviorStrategiesDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES);
  }

  openDataDrawer(behavior: CaseBehaviour): void {
    this.drawerService
      .openDrawer(
        InsightsDataComponent,
        this.drawerTitleData as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          case: this.caseParams.case,
          behavior,
          mode: InsightMode.VIEW,
          orgId: this.caseParams.case?.student?.organizationId,
          caseBehaviorId: behavior.id,
          isContinueProcessing: false,
        },
        DRAWER_IDS.MANAGE_DATA
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.drawerService.closeDrawer(DRAWER_IDS.DEFINE_BEHAVIOR_V2);
      });
  }

  closeDataDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_DATA);
  }

  openExampleOfGoals(): void {
    this.isGoalExampleModalVisible = true;
  }

  openPdfModal(): void {
    this.isPdfModalVisible = true;
  }

  get intensityName(): string | undefined {
    return this.behaviourIntensityList.find(
      item => item.id === this.form.controls['behaviourIntensityId'].value
    )?.name;
  }

  get isWarningModalVisible(): boolean {
    return (
      this.isIntensityWarningModalVisible &&
      (this.intensityName === 'Moderate' || this.intensityName === 'Major')
    );
  }

  showWarningModal = (): void => {
    this.isIntensityWarningModalVisible = true;
  };

  handleWarningOk(): void {
    this.isIntensityWarningModalVisible = false;
  }

  handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.showWarningModal();
    }
  };

  getBehaviorFrequencyLabel = (name: string): string => {
    return BehaviorFrequency[name as keyof typeof BehaviorFrequency] ?? name;
  };

  // Drives the Summary step (app-define-behavior-wizard-summary). Rebuilt
  // from the live form value + already-loaded lookup lists on every access
  // rather than a cached snapshot, since this wizard's fields (unlike the
  // legacy fixed wizard) are template-driven and never disabled/frozen.
  get summarySections(): WizardSummarySection[] {
    const contentSteps = this.steps.filter(
      step => step.kind !== WizardStepKind.Summary
    );
    const pendingHardStop = this.validationStatus?.pendingHardStopItems ?? [];
    const pendingRequired = this.validationStatus?.pendingRequiredItems ?? [];
    return contentSteps.map((step, index) => ({
      stepIndex: index,
      stepNumber: index + 1,
      totalSteps: contentSteps.length,
      title: step.label,
      isRequired: step.isRequired,
      isHardStop: step.isHardStop,
      isCategory: step.kind === WizardStepKind.BehaviorCategory,
      content: this.buildSummaryContent(step),
      pendingHardStopNames: this.stepPendingItems(step, pendingHardStop).map(
        item => item.name
      ),
      pendingRequiredNames: this.stepPendingItems(step, pendingRequired).map(
        item => item.name
      ),
    }));
  }

  // A "Section" pending item matches a step by native lookup id (refId); a
  // "CustomQuestion" one matches by membership in questionIds, which covers
  // both an EmbeddedCustomQuestion step (questionIds is just its own refId)
  // and a CustomQuestionBundle step (questionIds holds every bundled id) in
  // one check. "PlanType" items are plan-level (outside this wizard's own
  // Category/native/custom-question steps — see this directory's CLAUDE.md
  // on scope) and never correspond to a step here; without this explicit
  // branch they fell into the CustomQuestion check and could spuriously
  // match a step whose questionIds happened to collide with the plan type's
  // id. They still surface to the user via the unfiltered pending-name
  // banner in define-behavior-wizard-summary.component.html.
  private stepPendingItems(
    step: WizardStep,
    items: BehaviorPendingItem[]
  ): BehaviorPendingItem[] {
    return items.filter(item => {
      switch (item.itemType) {
        case 'Section':
          return step.refId === item.id;
        case 'CustomQuestion':
          return step.questionIds.includes(item.id);
        default:
          return false;
      }
    });
  }

  // Sourced from the form's live `draft` control (kept current on every save
  // by outstandingRequiredSteps()) rather than `behaviorDetail?.draft` like
  // bip-summary — behaviorDetail is only populated for edit/view loads, and
  // would stay stale/null through an in-progress create.
  get isBehaviorDraft(): boolean {
    return !!this.form.get('draft')?.value;
  }

  private buildSummaryContent(step: WizardStep): WizardSummaryContent {
    switch (step.kind) {
      case WizardStepKind.BehaviorCategory:
        return {
          items: [
            this.getCategoryNameById(
              this.form.get('behaviorCategoryId')?.value
            ) || 'Nothing entered',
          ],
        };
      case WizardStepKind.NativeField:
        return this.buildNativeSummaryContent(step.refId);
      case WizardStepKind.EmbeddedCustomQuestion:
        return {
          questions: [
            {
              label: this.questionLabel(step.refId),
              value:
                this.answerControlAt(step.refId)?.get('answer')?.value ||
                'Nothing entered',
            },
          ],
        };
      case WizardStepKind.CustomQuestionBundle:
        return {
          questions: step.questionIds.map(questionId => ({
            label: this.questionLabel(questionId),
            value:
              this.answerControlAt(questionId)?.get('answer')?.value ||
              'Nothing entered',
          })),
        };
      default:
        return {};
    }
  }

  private buildNativeSummaryContent(lookupId: number): WizardSummaryContent {
    switch (lookupId) {
      case this.lookupIds.CaseBehaviorTypes: {
        const selectedIds: number[] =
          this.form.get('caseBehaviorTypeIds')?.value || [];
        return {
          checkboxes: this.behaviourSubCategoryList
            .filter(item => selectedIds.includes(item.id))
            .map(item => ({ label: item.name, checked: true })),
          otherDescription: this.behaviorTypeDescriptionControls
            .map(ctrl => (ctrl.value.description || '').trim())
            .filter(Boolean),
        };
      }
      case this.lookupIds.OperationalDefinition: {
        const selectedIds: number[] =
          this.form.get('caseBehaviorTypeIds')?.value || [];
        return {
          badges: this.behaviourSubCategoryList
            .filter(item => selectedIds.includes(item.id))
            .map(item => item.name),
          description:
            this.form.get('operationalDefinition')?.value || 'Nothing entered',
        };
      }
      case this.lookupIds.Frequency: {
        const frequencyId = this.form.get('behaviourFrequencyId')?.value;
        const frequencyName = this.behaviourFrequencyList.find(
          item => item.id === frequencyId
        )?.name;
        return {
          fields: [
            {
              label:
                'Which of the following best describes the frequency of the behavior?',
              value: frequencyName
                ? this.getBehaviorFrequencyLabel(frequencyName)
                : 'Nothing entered',
            },
            {
              label: 'Once the behavior starts, how long does it usually last?',
              value: this.summaryDurationDisplay(),
            },
            {
              label: 'On average how many times does the behavior occur?',
              value:
                this.form.get('behaviourFrequencyCount')?.value ??
                'Nothing entered',
            },
          ],
        };
      }
      case this.lookupIds.Intensity: {
        const intensity = this.behaviourIntensityList.find(
          item => item.id === this.form.get('behaviourIntensityId')?.value
        );
        const intensityDescription = intensity?.description
          ? ` (${intensity.description})`
          : '';
        return {
          fields: [
            {
              label: 'How intense is the behavior when it occurs?',
              value: intensity
                ? `${intensity.name}${intensityDescription}`
                : 'Nothing entered',
            },
          ],
        };
      }
      case this.lookupIds.Environmnets: {
        const selectedIds: number[] =
          this.form.get('caseBehaviorEnvironmentIds')?.value || [];
        return {
          checkboxes: this.behaviourEnvironmentList
            .filter(item => selectedIds.includes(item.id))
            .map(item => ({ label: item.name, checked: true })),
        };
      }
      case this.lookupIds.SettingEvents: {
        const selectedIds: number[] =
          this.form.get('caseBehaviorSettingEventIds')?.value || [];
        return {
          checkboxes: this.behaviourSettingEventList
            .filter(item => selectedIds.includes(item.id))
            .map(item => ({ label: item.name, checked: true })),
        };
      }
      case this.lookupIds.Antecedents: {
        const selectedIds: number[] =
          this.form.get('caseBehaviorAntecedentIds')?.value || [];
        return {
          checkboxes: this.behaviourAntecedentList
            .filter(item => selectedIds.includes(item.id))
            .map(item => ({ label: item.name, checked: true })),
          otherDescription: this.antecedentDescriptionControls
            .map(ctrl => (ctrl.value.description || '').trim())
            .filter(Boolean),
        };
      }
      case this.lookupIds.Consequences: {
        const selectedIds: number[] =
          this.form.get('caseBehaviorConsequenceIds')?.value || [];
        return {
          checkboxes: this.behaviorConsequenceList
            .filter(item => selectedIds.includes(item.id))
            .map(item => ({ label: item.name, checked: true })),
          otherDescription: this.consequenceDescriptionControls
            .map(ctrl => (ctrl.value.description || '').trim())
            .filter(Boolean),
        };
      }
      case this.lookupIds.Goals: {
        const fields: { label: string; value: string | number }[] = [];
        if (this.behaviorGoal && this.behaviorGoal.statusId !== 2) {
          fields.push({
            label: 'District Instructions',
            value: this.behaviorGoal.templateInstructions || 'Nothing entered',
          });
        }
        fields.push({
          label: 'Goal',
          value: this.form.get('goals')?.value || 'Nothing entered',
        });
        return { fields };
      }
      default:
        return {};
    }
  }

  private summaryDurationDisplay(): string {
    const duration = this.form.get('duration')?.value;
    if (duration) {
      return `${duration} minutes`;
    }
    const frequencyId = this.form.get('behaviourFrequencyId')?.value;
    if (frequencyId) {
      return 'Duration does not matter or is instantaneous';
    }
    return 'Nothing entered';
  }
}
